import React, { useEffect } from 'react';

import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { normalizeFromPlanItem } from '@features/production/proposal/helpers';
import { usePlanCoversLovs } from '@features/production/proposal/hooks';
import type { IPolicyCoverForm } from '@features/production/proposal/types';

import { FormValues } from '../types/formValues.types';
import { AmendmentPolicyCoverForm } from '../types/getAmendmentsDetailsResponse.types';
import { CancellationPolicyCoverRowAtom } from './CancellationPolicyCoverRow.atom';

import { FieldArray, useFormikContext } from 'formik';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    margin: '20px auto 0',
    display: 'block'
  }
}));

type Props = {
  status: string;
  currencySymbol: string;
  planId: string;
  amendmentEffectiveDate?: string;
  planToPolicyRate: number;
  policyEffectiveFrom?: string;
  policyEffectiveTo?: string;
  allowPlanCoverEdits: boolean;
  disabledForm?: boolean;
  lineCode?: string;
  isGroup?: boolean;
};

const CancellationPolicyCoversWidgetAtom: React.FC<Props> = ({
  status,
  currencySymbol,
  planId,
  planToPolicyRate,
  amendmentEffectiveDate,
  policyEffectiveFrom,
  policyEffectiveTo,
  allowPlanCoverEdits,
  disabledForm,
  lineCode,
  isGroup
}) => {
  const { classes } = useStyles();
  const {
    values,
    initialValues,
    dirty,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    errors,
    touched
  } = useFormikContext<FormValues>();

  const rows = values?.covers ?? [];

  const liveAmendmentEffectiveDate =
    values?.amendmentDetails?.amendmentEffectiveDate || amendmentEffectiveDate;
  // Check if amendment type is cancellation
  const amendmentType = values?.amendmentDetails?.amendmentType || '';
  const isCancellation = amendmentType === 'PolicyCancellation';
  const rowsForLovs: IPolicyCoverForm[] = rows.map(
    (r): IPolicyCoverForm => ({
      policyPlanCoverId: r.policyPlanCoverId as any,
      policyCover: r.policyCover || '',
      isMain: !!r.isMain,
      isMandatory: !!r.isMandatory,
      isEditable: !!r.isEditable,
      coverEffectiveFrom: r.coverEffectiveFrom || '',
      coverEffectiveTo: r.coverEffectiveTo || '',
      sumInsuredIsAdditive: !!r.sumInsuredIsAdditive,
      coverSumInsured: r.coverSumInsured,
      coverPremiumType: r.coverPremiumType as any,
      coverPremiumAmount: r.coverPremiumAmount,
      coverPremiumPercentage: r.coverPremiumPercentage,
      excessOnClaimType: r.excessOnClaimType as any,
      excessOnClaimAmount: r.excessOnClaimAmount,
      excessOnClaimPercentage: r.excessOnClaimPercentage,
      excessOnClaimDays: r.excessOnClaimDays
    })
  );

  const { rawItems, filteredForIndex } = usePlanCoversLovs(
    planId,
    rowsForLovs,
    planToPolicyRate
  );

  const maxRows = rawItems?.length ?? 0;
  const isLine51 = String(lineCode ?? '') === '51';

  // useEffect(() => {
  //   rows.forEach((row, index) => {
  //     const calculateProRata = (coverTo: string, coverFrom: string) => {
  //       const d = (s?: string) => (s ? new Date(s).getTime() : NaN);
  //       const to = d(coverTo);
  //       const from = d(coverFrom);
  //       const pTo = d(policyEffectiveTo);
  //       const pFrom = d(policyEffectiveFrom);

  //       if (
  //         Number.isNaN(to) ||
  //         Number.isNaN(from) ||
  //         Number.isNaN(pTo) ||
  //         Number.isNaN(pFrom)
  //       ) {
  //         return 0;
  //       }

  //       const top = Math.max(0, to - from);
  //       const bottom = Math.max(1, pTo - pFrom);
  //       const ratio = +(top / bottom);

  //       return ratio;
  //     };

  //     if (row.isNew && row.coverEffectiveFrom !== liveAmendmentEffectiveDate) {
  //       setFieldValue(
  //         `covers[${index}].coverEffectiveFrom`,
  //         liveAmendmentEffectiveDate,
  //         false
  //       );

  //       const newProRata = calculateProRata(
  //         policyEffectiveTo || '',
  //         liveAmendmentEffectiveDate || ''
  //       );

  //       setFieldValue(`covers[${index}].proRataRatio`, newProRata, false);

  //       if (
  //         row.coverPremiumType === 'AMOUNT' &&
  //         row.coverPremiumAmount != null
  //       ) {
  //         const basePremium = row.coverPremiumAmount;
  //         const calculatedPremium = +(basePremium * newProRata).toFixed(3);

  //         setFieldValue(
  //           `covers[${index}].coverPremiumAmount`,
  //           calculatedPremium,
  //           false
  //         );
  //         setFieldValue(
  //           `covers[${index}].newPremiumAmount`,
  //           calculatedPremium,
  //           false
  //         );
  //       }
  //     }

  //     if (
  //       row.isRemoved &&
  //       !row.isNew &&
  //       row.coverEffectiveTo !== liveAmendmentEffectiveDate
  //     ) {
  //       setFieldValue(
  //         `covers[${index}].coverEffectiveTo`,
  //         liveAmendmentEffectiveDate,
  //         false
  //       );

  //       const newProRata = calculateProRata(
  //         liveAmendmentEffectiveDate || '',
  //         row.coverEffectiveFrom || ''
  //       );

  //       setFieldValue(`covers[${index}].proRataRatio`, newProRata, false);

  //       if (
  //         row.coverPremiumType === 'AMOUNT' &&
  //         row.coverPremiumAmount != null
  //       ) {
  //         const newPremiumAmountComputed = (
  //           row.coverPremiumAmount * newProRata
  //         ).toFixed(3);

  //         setFieldValue(
  //           `covers[${index}].newPremiumAmount`,
  //           newPremiumAmountComputed,
  //           false
  //         );
  //       }

  //       if (row.coverPremiumType === 'PERCENTAGE') {
  //         setFieldValue(
  //           `covers[${index}].newPremiumPercentage`,
  //           row.coverPremiumPercentage,
  //           false
  //         );
  //       }
  //     }
  //   });
  // }, [
  //   liveAmendmentEffectiveDate,
  //   rows,
  //   setFieldValue,
  //   policyEffectiveFrom,
  //   policyEffectiveTo
  // ]);

  const applyDefaults = async (
    rowIndex: number,
    selectedIdFromChange?: string
  ) => {
    const prevAmend = rows[rowIndex];
    const sel = String(selectedIdFromChange ?? prevAmend?.policyCover ?? '');
    if (!sel) return;

    const fromPlan = rawItems.find((it: any) => String(it?.id) === sel);
    if (!fromPlan) return;

    const prevProposal = rowsForLovs[rowIndex];
    const normalizedFromPlan = normalizeFromPlanItem(fromPlan, prevProposal);
    const base = `covers[${rowIndex}]` as const;

    await setFieldValue(`${base}.policyCover`, sel, false);
    await setFieldTouched(`${base}.policyCover`, true, false);

    await setFieldValue(
      `${base}.policyCoverName`,
      `${fromPlan?.cover?.code ?? ''} - ${fromPlan?.cover?.name ?? ''}`,
      false
    );

    await setFieldValue(
      `${base}.coverEffectiveFrom`,
      liveAmendmentEffectiveDate,
      false
    );
    await setFieldTouched(`${base}.coverEffectiveFrom`, true, false);
    await setFieldValue(`${base}.coverEffectiveTo`, policyEffectiveTo, false);

    const calculateProRataForNewCover = () => {
      const d = (s?: string) => (s ? new Date(s).getTime() : NaN);
      const coverTo = d(policyEffectiveTo);
      const coverFrom = d(liveAmendmentEffectiveDate);
      const policyTo = d(policyEffectiveTo);
      const policyFrom = d(policyEffectiveFrom);

      if (
        Number.isNaN(coverTo) ||
        Number.isNaN(coverFrom) ||
        Number.isNaN(policyTo) ||
        Number.isNaN(policyFrom)
      ) {
        return 1;
      }

      const top = Math.max(0, coverTo - coverFrom);
      const bottom = Math.max(1, policyTo - policyFrom);

      return +(top / bottom);
    };

    const proRataRatio = calculateProRataForNewCover();

    const set = (k: keyof AmendmentPolicyCoverForm, v: any) =>
      setFieldValue(`${base}.${String(k)}`, v, false);

    await set(
      'sumInsuredIsAdditive',
      !!normalizedFromPlan.sumInsuredIsAdditive
    );
    await set('coverSumInsured', normalizedFromPlan.coverSumInsured);
    await set('coverPremiumType', normalizedFromPlan.coverPremiumType as any);
    await set(
      'coverPremiumPercentage',
      normalizedFromPlan.coverPremiumPercentage
    );

    const calculatedPremiumAmount =
      normalizedFromPlan.coverPremiumAmount != null
        ? +(normalizedFromPlan.coverPremiumAmount * proRataRatio).toFixed(3)
        : undefined;

    await set('coverPremiumAmount', calculatedPremiumAmount);

    await set('excessOnClaimType', normalizedFromPlan.excessOnClaimType as any);
    await set(
      'excessOnClaimPercentage',
      normalizedFromPlan.excessOnClaimPercentage
    );
    await set('excessOnClaimAmount', normalizedFromPlan.excessOnClaimAmount);
    await set('excessOnClaimDays', normalizedFromPlan.excessOnClaimDays);

    await set('isMain', !!normalizedFromPlan.isMain);
    await set('isMandatory', !!normalizedFromPlan.isMandatory);

    await set('isNew', true);
    await set('isRemoved', false);
    await set('proRataRatio', proRataRatio);

    await set('newSumInsured', normalizedFromPlan.coverSumInsured);
    await set(
      'newPremiumPercentage',
      normalizedFromPlan.coverPremiumPercentage
    );
    await set('newPremiumAmount', calculatedPremiumAmount);

    await set('newExcessDays', normalizedFromPlan.excessOnClaimDays);
    await set('newExcessAmount', normalizedFromPlan.excessOnClaimAmount);
    await set(
      'newExcessPercentage',
      normalizedFromPlan.excessOnClaimPercentage
    );

    await set('isEditable', !!normalizedFromPlan.isEditable);

    // Initialize premiumVariance for cancellation scenarios
    await set('premiumVariance', undefined);
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Policy Covers" useSeparator={false}>
        <FieldArray
          name="covers"
          render={(arrayHelpers) => (
            <>
              {rows.map((row, rowIndex) => {
                const disablePolicyCoverSelect =
                  !!disabledForm || !!row.isMandatory || !row.isNew;
                const options = filteredForIndex(rowIndex);

                const fromPlan = rawItems?.find(
                  (it: any) => String(it?.id) === String(row.policyCover)
                );

                let effectiveIsEditable = row.isEditable;
                if (fromPlan) {
                  const normalizedFromPlan = normalizeFromPlanItem(
                    fromPlan,
                    rowsForLovs[rowIndex]
                  );
                  effectiveIsEditable = !!normalizedFromPlan.isEditable;
                }

                const uiRow: AmendmentPolicyCoverForm = {
                  ...row,
                  isEditable: effectiveIsEditable
                };

                // Calculate if premium variance field is dirty for this row
                const isPremiumVarianceDirty =
                  dirty &&
                  (row as any).coverPremiumVariance !==
                  (initialValues?.covers?.[rowIndex] as any)
                    ?.coverPremiumVariance &&
                  (row as any).coverPremiumVariance !== undefined &&
                  (row as any).coverPremiumVariance !== '';

                return (
                  <CancellationPolicyCoverRowAtom
                    key={rowIndex}
                    row={uiRow}
                    rowIndex={rowIndex}
                    disabledForm={!!disabledForm}
                    allowPlanCoverEdits={!!allowPlanCoverEdits}
                    amendmentStatus={status || ''}
                    currencySymbol={currencySymbol}
                    policyEffectiveDate={policyEffectiveFrom}
                    policyExpiryDate={policyEffectiveTo}
                    amendmentEffectiveDate={liveAmendmentEffectiveDate}
                    selectOptions={options}
                    disablePolicyCoverSelect={disablePolicyCoverSelect}
                    onRemove={
                      uiRow.isMandatory || disabledForm || !allowPlanCoverEdits
                        ? undefined
                        : () => arrayHelpers.remove(rowIndex)
                    }
                    touched={touched}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    handleBlur={handleBlur}
                    onBlurPolicyCover={(selectedId?: string) =>
                      applyDefaults(rowIndex, selectedId)
                    }
                    isLine51={isLine51}
                    isGroup={isGroup}
                    isCancellation={isCancellation}
                    isPremiumVarianceDirty={!!isPremiumVarianceDirty}
                  />
                );
              })}
            </>
          )}
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export { CancellationPolicyCoversWidgetAtom };
